import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Timestamp, doc, updateDoc } from 'firebase/firestore'
import { db } from '../../firebase'
import {
  buildFactionMemberFields,
  getFactionStoredWrestlers
} from '../../utils/factionWrestlers'
import { settleFactionMembersForStatusChanges } from '../../utils/factionScoring'
import {
  buildFactionLineupUpdateAfterSwap,
  getFactionMainEventerCooldownUntil
} from '../../utils/factionLineup'
import {
  addDays,
  getMainEventerCooldownDays,
  getMainEventerDurationDays,
  startOfDay,
  toDate
} from '../../utils/mainEventerCooldown'
import {
  applyBulkLineupAction,
  getLineupMemberStatus
} from '../../utils/lineupBulkEditing'
import {
  PremiumFeaturePill,
  PremiumWrestlerInsightButton
} from '../premium/PremiumInfoButton'
import PremiumFeaturePreview from '../premium/PremiumFeaturePreview'
import {
  buildDismissibleBannerKey,
  hideDismissibleBanner,
  isDismissibleBannerHidden
} from '../../utils/dismissibleBannerPrefs'
import { buildLineupRoleAdvice } from '../../utils/autoLineupAdvice'

import beltImg from '../../assets/me_title.png'
import ringImg from '../../assets/ring.png'
import backstageImg from '../../assets/backstage.jpg'
import vacantImg from '../../assets/vacant.png'

const toLineupAdviceNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

const formatLineupAdvicePoints = points =>
  `${toLineupAdviceNumber(points)} pt ultime 3`

const formatLineupAdviceDate = value => {
  const date = toDate(value)
  return date
    ? date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit'
      })
    : ''
}

const formatUpcomingPleAdvice = upcomingPleMatch => {
  if (!upcomingPleMatch) return ''

  const showName = upcomingPleMatch.showName || 'PLE'
  const dateLabel = formatLineupAdviceDate(upcomingPleMatch.date)
  return `Match PLE annunciato: ${showName}${dateLabel ? ` (${dateLabel})` : ''}`
}

const getLineupAdviceReason = advice =>
  (advice?.primary === 'backstage' && advice?.isInactive
    ? 'Wrestler non attivo'
    : '') ||
  formatUpcomingPleAdvice(advice?.upcomingPleMatch) ||
  formatLineupAdvicePoints(advice?.recentPoints)

const getLineupAdviceLabel = (advice, compact = false) => {
  if (!advice) return ''
  if (advice.primary === 'main-eventer')
    return compact ? 'Main' : 'Main Eventer'
  if (advice.primary === 'backstage') return 'Backstage'
  return 'In-Ring'
}

const getLineupAdviceText = advice => {
  const label = getLineupAdviceLabel(advice)
  const upcomingPleAdvice = formatUpcomingPleAdvice(advice?.upcomingPleMatch)

  if (upcomingPleAdvice && advice?.primary === 'main-eventer') {
    return `${label} - ${upcomingPleAdvice}`
  }

  if (advice?.primary === 'backstage' && advice?.isInactive) {
    return `${label} - wrestler non attivo`
  }

  return `${label} consigliato`
}

const getLineupSlotMeta = (type, index = null) => {
  if (type === 'champion') {
    return {
      title: 'Main Eventer',
      description:
        'Scegli il volto principale della line-up. Deve gia essere schierato In-Ring.',
      icon: 'bi bi-trophy-fill'
    }
  }

  if (type === 'ring') {
    return {
      title: `In-Ring ${typeof index === 'number' ? index + 1 : ''}`.trim(),
      description:
        'Assegna uno slot attivo della card. Qui conviene mettere i wrestler piu pronti a fare punti.',
      icon: 'bi bi-broadcast-pin'
    }
  }

  return {
    title: `Backstage ${typeof index === 'number' ? index + 1 : ''}`.trim(),
    description:
      'Usa il backstage per wrestler non attivi, infortunati o con meno upside immediato.',
    icon: 'bi bi-door-open-fill'
  }
}

const formatLineupAdviceNames = (items = [], limit = 3) => {
  const names = items.map(item => item?.member?.name).filter(Boolean)
  if (names.length <= limit) return names.join(', ')
  return `${names.slice(0, limit).join(', ')} +${names.length - limit}`
}

function EditFactionFormLineUp ({
  faction,
  members,
  roster,
  onSaved = null,
  isPremium = false,
  premiumInsights = {},
  premiumInsightsLoading = false
}) {
  const totalWrestlers = members.length
  const lineupConfig = useMemo(() => roster || faction || {}, [faction, roster])
  const mainEventerDurationDays = getMainEventerDurationDays(lineupConfig)
  const mainEventerCooldownDays = getMainEventerCooldownDays(lineupConfig)
  const hasMainEventer = lineupConfig.hasMainEventer !== false
  const hasBackstage =
    Boolean(lineupConfig.hasBackstage) ||
    Number(lineupConfig.backstageCount || 0) > 0
  const configuredBackstage = Number.isInteger(lineupConfig?.backstageCount)
    ? lineupConfig.backstageCount
    : Math.max(0, Math.floor(totalWrestlers / 2) - 1)
  const maxBackstage = hasBackstage
    ? Math.max(
        0,
        Math.min(
          configuredBackstage,
          Math.max(0, totalWrestlers - (hasMainEventer ? 1 : 0))
        )
      )
    : 0
  const maxRing = Math.max(0, totalWrestlers - maxBackstage)

  const emptyLineup = useMemo(
    () => ({
      champion: null,
      inRing: Array(maxRing).fill(null),
      backstage: Array(maxBackstage).fill(null),
      mainEventerAssignedAt: null,
      mainEventerExpiresAt: null
    }),
    [maxBackstage, maxRing]
  )

  const [lineup, setLineup] = useState(emptyLineup)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [modalSlotIndex, setModalSlotIndex] = useState(null)
  const [currentSlotWrestler, setCurrentSlotWrestler] = useState(null)
  const [bulkModeEnabled, setBulkModeEnabled] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [dragOverTarget, setDragOverTarget] = useState('')
  const [touchDragPreview, setTouchDragPreview] = useState(null)
  const [injuryBannerClosed, setInjuryBannerClosed] = useState(false)
  const [injuryBannerHidden, setInjuryBannerHidden] = useState(false)
  const touchDragRef = useRef({
    wrestlerId: null,
    ids: [],
    dragging: false,
    suppressClick: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0
  })

  const normalizeLineup = useMemo(
    () => sourceLineup => {
      const sourceInRing = [
        ...(sourceLineup?.inRing || sourceLineup?.inRingIds || [])
      ].filter(Boolean)
      const sourceBackstage = [
        ...(sourceLineup?.backstage || sourceLineup?.backstageIds || [])
      ].filter(Boolean)
      const champion =
        sourceLineup?.champion || sourceLineup?.championId || null

      const nextInRing = [...sourceInRing]
      if (champion && !nextInRing.includes(champion)) {
        nextInRing.unshift(champion)
      }

      const dedupedInRing = [...new Set(nextInRing)].slice(0, maxRing)
      const dedupedBackstage = sourceBackstage
        .filter(wrestlerId => !dedupedInRing.includes(wrestlerId))
        .slice(0, maxBackstage)

      while (dedupedInRing.length < maxRing) dedupedInRing.push(null)
      while (dedupedBackstage.length < maxBackstage) dedupedBackstage.push(null)

      const assignedAt = toDate(
        sourceLineup?.mainEventerAssignedAt ||
          sourceLineup?.currentMainEventerAssignedAt ||
          sourceLineup?.effectiveFrom
      )
      const expiresAt = toDate(sourceLineup?.mainEventerExpiresAt)

      return {
        champion:
          hasMainEventer && champion && dedupedInRing.includes(champion)
            ? champion
            : null,
        inRing: dedupedInRing,
        backstage: dedupedBackstage,
        mainEventerAssignedAt: hasMainEventer ? assignedAt : null,
        mainEventerExpiresAt:
          hasMainEventer && champion && assignedAt
            ? expiresAt ||
              addDays(startOfDay(assignedAt), mainEventerDurationDays)
            : null
      }
    },
    [hasMainEventer, mainEventerDurationDays, maxBackstage, maxRing]
  )

  const clearExpiredChampion = useMemo(
    () => sourceLineup => {
      if (!sourceLineup.champion || !sourceLineup.mainEventerExpiresAt)
        return sourceLineup

      const now = new Date()
      if (sourceLineup.mainEventerExpiresAt.getTime() > now.getTime())
        return sourceLineup

      return {
        ...sourceLineup,
        champion: null,
        mainEventerAssignedAt: null,
        mainEventerExpiresAt: null
      }
    },
    []
  )

  const savedLineupSnapshot = useMemo(() => {
    let sourceLineup = null

    if (faction?.currentLineup) {
      sourceLineup = normalizeLineup(faction.currentLineup)
    } else if (faction?.lineup) {
      sourceLineup = normalizeLineup(faction.lineup)
    } else if (getFactionStoredWrestlers(faction).length) {
      const factionWrestlers = getFactionStoredWrestlers(faction)
      const champion = hasMainEventer
        ? factionWrestlers.find(cost => cost.status === 'Main_Eventer')?.id ||
          null
        : null
      const inRing = factionWrestlers
        .filter(
          cost => cost.status === 'In_Ring' || cost.status === 'Main_Eventer'
        )
        .map(cost => cost.id)
      const backstage = factionWrestlers
        .filter(cost => cost.status === 'Backstage')
        .map(cost => cost.id)

      sourceLineup = normalizeLineup({ champion, inRing, backstage })
    } else {
      sourceLineup = normalizeLineup(emptyLineup)
    }

    return sourceLineup
  }, [emptyLineup, faction, hasMainEventer, normalizeLineup])

  const persistedLineup = useMemo(
    () => clearExpiredChampion(savedLineupSnapshot),
    [clearExpiredChampion, savedLineupSnapshot]
  )

  useEffect(() => {
    setLineup(persistedLineup)
  }, [persistedLineup])

  const getWrestler = useCallback(
    id => members.find(member => member.id === id),
    [members]
  )

  const getCurrentStatus = useCallback(
    wrestlerId => getLineupMemberStatus(lineup, wrestlerId),
    [lineup]
  )

  const assignedInRingIds = lineup.inRing.filter(Boolean)
  const assignedBackstageIds = lineup.backstage.filter(Boolean)
  const assignedIds = useMemo(
    () => new Set([...assignedInRingIds, ...assignedBackstageIds]),
    [assignedBackstageIds, assignedInRingIds]
  )

  const unassignedWrestlers = useMemo(
    () => members.filter(member => !assignedIds.has(member.id)),
    [assignedIds, members]
  )
  const hasSavedLineup = useMemo(
    () =>
      Boolean(
        persistedLineup?.champion ||
          persistedLineup?.inRing?.some(Boolean) ||
          persistedLineup?.backstage?.some(Boolean)
      ),
    [persistedLineup]
  )
  const ringSlotsLeft = Math.max(0, maxRing - assignedInRingIds.length)
  const backstageSlotsLeft = Math.max(
    0,
    maxBackstage - assignedBackstageIds.length
  )
  const isInitialPlacementFlow = !hasSavedLineup
  const wrestlersLeftToPlace = unassignedWrestlers.length
  const injuryBannerKey = useMemo(
    () => buildDismissibleBannerKey('faction-injuries', faction?.id || 'global'),
    [faction?.id]
  )

  const injuredMembers = useMemo(
    () => members.filter(member => member?.isInjured),
    [members]
  )
  const injuredMembersOutsideBackstage = useMemo(
    () =>
      injuredMembers.filter(
        member => !assignedBackstageIds.includes(member.id)
      ),
    [assignedBackstageIds, injuredMembers]
  )
  const injuredMembersLabel = useMemo(() => {
    const source =
      hasBackstage && injuredMembersOutsideBackstage.length > 0
        ? injuredMembersOutsideBackstage
        : injuredMembers
    const names = source
      .map(member => member?.name)
      .filter(Boolean)
      .slice(0, 3)

    if (source.length > 3) {
      names.push(`+${source.length - 3}`)
    }

    return names.join(', ')
  }, [hasBackstage, injuredMembers, injuredMembersOutsideBackstage])
  const showInjuryBanner =
    injuredMembers.length > 0 && !injuryBannerClosed && !injuryBannerHidden

  useEffect(() => {
    setInjuryBannerClosed(false)
    setInjuryBannerHidden(isDismissibleBannerHidden(injuryBannerKey))
  }, [injuryBannerKey])

  const handleHideInjuryBannerPermanently = () => {
    hideDismissibleBanner(injuryBannerKey)
    setInjuryBannerHidden(true)
    setInjuryBannerClosed(true)
  }

  const isComplete = () => assignedIds.size === totalWrestlers

  const availableWrestlers = useMemo(
    () => members.filter(member => !assignedIds.has(member.id)),
    [assignedIds, members]
  )

  const filteredMembers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    const source = [...members].sort((a, b) => {
      const statusPriority = {
        Main_Eventer: 0,
        In_Ring: 1,
        Backstage: 2,
        null: 3
      }
      const statusA = getCurrentStatus(a.id)
      const statusB = getCurrentStatus(b.id)
      const priorityA = statusPriority[statusA ?? 'null']
      const priorityB = statusPriority[statusB ?? 'null']

      if (priorityA !== priorityB) return priorityA - priorityB
      return (a.name || '').localeCompare(b.name || '', 'it')
    })

    if (!normalizedSearch) return source

    return source.filter(member =>
      [member.name, member.brand]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(normalizedSearch))
    )
  }, [getCurrentStatus, members, searchTerm])

  const selectedMembers = useMemo(
    () => members.filter(member => selectedIds.includes(member.id)),
    [members, selectedIds]
  )

  const championCandidates = useMemo(() => {
    const currentChampion = lineup.champion
      ? getWrestler(lineup.champion)
      : null
    const inRingWrestlers = assignedInRingIds
      .map(wrestlerId => getWrestler(wrestlerId))
      .filter(Boolean)

    return currentChampion &&
      !inRingWrestlers.some(wrestler => wrestler.id === currentChampion.id)
      ? [currentChampion, ...inRingWrestlers]
      : inRingWrestlers
  }, [assignedInRingIds, getWrestler, lineup.champion])

  const resolveMainEventerCooldownUntil = useCallback(
    wrestlerId =>
      getFactionMainEventerCooldownUntil(
        faction,
        wrestlerId,
        new Date(),
        lineupConfig
      ),
    [faction, lineupConfig]
  )
  const buildMainEventerPreview = useCallback(
    (referenceDate = new Date()) => {
      const normalizedReference = toDate(referenceDate) || new Date()
      const expiresAt = addDays(
        startOfDay(normalizedReference),
        mainEventerDurationDays
      )

      return {
        expiresAt,
        availableAgainAt: addDays(
          startOfDay(expiresAt),
          mainEventerCooldownDays
        )
      }
    },
    [mainEventerCooldownDays, mainEventerDurationDays]
  )

  const savedChampionId = savedLineupSnapshot?.champion || null
  const savedChampion = useMemo(
    () => (savedChampionId ? getWrestler(savedChampionId) : null),
    [getWrestler, savedChampionId]
  )
  const draftChampion = useMemo(
    () => (lineup.champion ? getWrestler(lineup.champion) : null),
    [getWrestler, lineup.champion]
  )
  const hasPendingChampionChange =
    (lineup.champion || null) !== (persistedLineup?.champion || null)
  const savedChampionExpiryText = useMemo(() => {
    if (!savedLineupSnapshot?.mainEventerExpiresAt) return ''

    const expiryDate = savedLineupSnapshot.mainEventerExpiresAt
    const hasExpired = expiryDate.getTime() < new Date().getTime()

    return `${
      hasExpired ? 'Era scaduto' : 'Scade'
    } il ${expiryDate.toLocaleDateString()}`
  }, [savedLineupSnapshot])
  const savedChampionCooldownUntil = useMemo(
    () => resolveMainEventerCooldownUntil(savedChampionId),
    [resolveMainEventerCooldownUntil, savedChampionId]
  )
  const draftChampionPreview = useMemo(() => {
    if (!lineup.champion) return null

    if (
      lineup.champion === persistedLineup?.champion &&
      persistedLineup?.mainEventerExpiresAt
    ) {
      return {
        expiresAt: persistedLineup.mainEventerExpiresAt,
        availableAgainAt: addDays(
          startOfDay(persistedLineup.mainEventerExpiresAt),
          mainEventerCooldownDays
        )
      }
    }

    return buildMainEventerPreview(new Date())
  }, [
    buildMainEventerPreview,
    lineup.champion,
    mainEventerCooldownDays,
    persistedLineup?.champion,
    persistedLineup?.mainEventerExpiresAt
  ])

  const lineupRoleAdvice = useMemo(() => buildLineupRoleAdvice({
    isPremium,
    premiumInsightsLoading,
    members,
    premiumInsights,
    hasMainEventer,
    hasBackstage,
    maxBackstage,
    maxRing,
    resolveMainEventerCooldownUntil
  }), [
    hasBackstage,
    hasMainEventer,
    isPremium,
    maxBackstage,
    maxRing,
    members,
    premiumInsights,
    premiumInsightsLoading,
    resolveMainEventerCooldownUntil
  ])

  const getLineupAdviceForWrestler = useCallback(
    wrestlerId => lineupRoleAdvice.byId[wrestlerId] || null,
    [lineupRoleAdvice]
  )

  const renderLineupAdviceCard = (className = '') => {
    if (!isPremium || !lineupRoleAdvice.hasAny) return null

    const mainEventerPleAlternatives =
      lineupRoleAdvice.mainEventer && lineupRoleAdvice.mainEventerPleCandidates
        ? lineupRoleAdvice.mainEventerPleCandidates.filter(
            item => item.member.id !== lineupRoleAdvice.mainEventer.member.id
          )
        : []

    return (
      <div className={`lineup-advice-card ${className}`.trim()}>
        <div className='lineup-advice-card__header'>
          <PremiumFeaturePill label='Consigli Premium' />
        </div>
        <div className='lineup-advice-card__grid'>
          {hasMainEventer && lineupRoleAdvice.mainEventer && (
            <div className='lineup-advice-card__item lineup-advice-card__item--main'>
              <small>Main Eventer</small>
              <strong>{lineupRoleAdvice.mainEventer.member.name}</strong>
              <span>{getLineupAdviceReason(lineupRoleAdvice.mainEventer)}</span>
              {lineupRoleAdvice.mainEventer.upcomingPleMatch && (
                <span className='lineup-advice-card__note'>
                  {formatLineupAdvicePoints(
                    lineupRoleAdvice.mainEventer.recentPoints
                  )}
                </span>
              )}
              {mainEventerPleAlternatives.length > 0 && (
                <span className='lineup-advice-card__note'>
                  Altri PLE:{' '}
                  {formatLineupAdviceNames(mainEventerPleAlternatives)}
                </span>
              )}
            </div>
          )}
          {lineupRoleAdvice.inRing.length > 0 && (
            <div className='lineup-advice-card__item'>
              <small>In-Ring</small>
              <strong>
                {formatLineupAdviceNames(lineupRoleAdvice.inRing)}
              </strong>
              <span>Rendimento recente piu alto</span>
            </div>
          )}
          {hasBackstage && lineupRoleAdvice.backstage.length > 0 && (
            <div className='lineup-advice-card__item lineup-advice-card__item--backstage'>
              <small>Backstage</small>
              <strong>
                {formatLineupAdviceNames(lineupRoleAdvice.backstage)}
              </strong>
              <span>Non attivi, meno punti recenti o infortunati</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const lineupPremiumPreview = useMemo(() => {
    const previewNames = members.map(member => member?.name).filter(Boolean)
    const mainEventerName = previewNames[0] || 'Main Eventer ideale'
    const inRingNames =
      previewNames.slice(1, 3).join(', ') || 'Slot ad alto potenziale'
    const backstageNames =
      previewNames.slice(3, 5).join(', ') || 'Gestione riposo e infortuni'
    const insightName = previewNames[1] || 'Insight wrestler'

    return {
      mainEventerName,
      inRingNames,
      backstageNames,
      insightName
    }
  }, [members])

  const renderLineupPremiumPreview = () => {
    if (isPremium) return null

    return (
      <PremiumFeaturePreview
        previewId='faction-lineup-premium-preview'
        scope={faction?.id || 'global'}
        title='Anteprima line-up Premium'
        description='Uno sguardo veloce a auto line-up, ruoli consigliati e insight dedicati ai wrestler.'
        overlayTitle='Auto line-up, ruoli consigliati e insight wrestler'
        overlayText='Scopri come ottimizzare la tua line-up con suggerimenti e insight dedicati ai wrestler.'
        featureList={['Auto line-up', 'Ruoli consigliati', 'Insight wrestler']}
        className='mb-4'
      >
        <div className='premium-preview-card__mock-stack'>
          <div className='lineup-premium-actions'>
            <button
              type='button'
              className='btn btn-sm btn-outline-brand-secondary lineup-auto-button'
              disabled
            >
              <i className='bi bi-stars me-1' aria-hidden='true'></i>
              Auto line-up
            </button>
          </div>

          <div className='lineup-advice-card lineup-advice-card--bottom mt-3'>
            <div className='lineup-advice-card__header'>
              <PremiumFeaturePill label='Consigli Premium' />
            </div>
            <div className='lineup-advice-card__grid'>
              {hasMainEventer ? (
                <div className='lineup-advice-card__item lineup-advice-card__item--main'>
                  <small>Main Eventer</small>
                  <strong>{lineupPremiumPreview.mainEventerName}</strong>
                  <span>Match PLE annunciato e rendimento recente</span>
                </div>
              ) : (
                <div className='lineup-advice-card__item'>
                  <small>Insight</small>
                  <strong>{lineupPremiumPreview.insightName}</strong>
                  <span>Dettagli premium wrestler per wrestler</span>
                </div>
              )}
              <div className='lineup-advice-card__item'>
                <small>In-Ring</small>
                <strong>{lineupPremiumPreview.inRingNames}</strong>
                <span>Slot con rendimento potenziale piu alto</span>
              </div>
              {hasBackstage ? (
                <div className='lineup-advice-card__item lineup-advice-card__item--backstage'>
                  <small>Backstage</small>
                  <strong>{lineupPremiumPreview.backstageNames}</strong>
                  <span>Riposo mirato per non attivi o infortunati</span>
                </div>
              ) : (
                <div className='lineup-advice-card__item'>
                  <small>Insight</small>
                  <strong>{lineupPremiumPreview.insightName}</strong>
                  <span>Dettagli premium wrestler per wrestler</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </PremiumFeaturePreview>
    )
  }

  const getSlotCurrentId = useCallback(
    (type, index = null) => {
      if (type === 'champion') return lineup.champion
      if (type === 'ring' && index !== null) return lineup.inRing[index]
      if (type === 'backstage' && index !== null) return lineup.backstage[index]
      return null
    },
    [lineup]
  )

  const toggleBulkMode = useCallback(enabled => {
    setBulkModeEnabled(enabled)
    setDragOverTarget('')

    if (!enabled) {
      setSelectedIds([])
      setSearchTerm('')
    }
  }, [])

  const buildActionSuccessMessage = useCallback((ids, targetLabel) => {
    if (!targetLabel) return ''

    return ids.length === 1
      ? `Wrestler spostato in ${targetLabel}.`
      : `${ids.length} wrestler spostati in ${targetLabel}.`
  }, [])

  const handleSlotClick = (type, index = null) => {
    const currentId = getSlotCurrentId(type, index)

    if (bulkModeEnabled && currentId) {
      setSelectedIds(prev =>
        prev.includes(currentId)
          ? prev.filter(id => id !== currentId)
          : [...prev, currentId]
      )
      setError('')
      setSuccess('')
      return
    }

    setCurrentSlotWrestler(currentId ? getWrestler(currentId) : null)
    setModalType(type)
    setModalSlotIndex(index)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalType(null)
    setModalSlotIndex(null)
    setCurrentSlotWrestler(null)
  }

  const openBulkEditor = () => {
    closeModal()
    toggleBulkMode(true)
  }

  const setChampion = useCallback(
    (wrestlerId, baseLineup = lineup) => {
      const nextLineup = {
        ...baseLineup,
        champion: wrestlerId,
        mainEventerAssignedAt:
          persistedLineup?.champion === wrestlerId
            ? persistedLineup.mainEventerAssignedAt || null
            : null,
        mainEventerExpiresAt:
          persistedLineup?.champion === wrestlerId
            ? persistedLineup.mainEventerExpiresAt || null
            : null
      }

      setLineup(nextLineup)
      return nextLineup
    },
    [lineup, persistedLineup]
  )

  const trySetChampion = (
    wrestlerId,
    baseLineup = lineup,
    keepSelection = false
  ) => {
    const cooldownUntil = resolveMainEventerCooldownUntil(wrestlerId)

    if (cooldownUntil) {
      const wrestlerName = getWrestler(wrestlerId)?.name || 'Questo wrestler'
      setError(
        `${wrestlerName} è in pausa da Main Eventer fino al ${cooldownUntil.toLocaleDateString()}.`
      )
      return false
    }

    setChampion(wrestlerId, baseLineup)
    if (keepSelection) {
      setSelectedIds([wrestlerId])
    }
    setError('')
    setSuccess('')
    return true
  }

  const toggleSelectedId = wrestlerId => {
    setSelectedIds(prev =>
      prev.includes(wrestlerId)
        ? prev.filter(id => id !== wrestlerId)
        : [...prev, wrestlerId]
    )
    setError('')
    setSuccess('')
  }

  const selectAllVisible = () => {
    setSelectedIds(prev => [
      ...new Set([...prev, ...filteredMembers.map(member => member.id)])
    ])
    setError('')
    setSuccess('')
  }

  const clearSelection = () => {
    setSelectedIds([])
    setError('')
    setSuccess('')
  }

  const handleQuickAssign = (wrestlerId, targetStatus) => {
    applyLineupActionForIds([wrestlerId], targetStatus, {
      keepSelection: false,
      successMessage: buildActionSuccessMessage(
        [wrestlerId],
        targetStatus === 'In_Ring' ? 'In-Ring' : 'Backstage'
      )
    })
  }

  const applyLineupActionForIds = (
    ids,
    targetStatus,
    { keepSelection = true, successMessage = '' } = {}
  ) => {
    const normalizedIds = [...new Set((ids || []).filter(Boolean))]
    const result = applyBulkLineupAction({
      lineup,
      selectedIds: normalizedIds,
      targetStatus,
      maxRing,
      maxBackstage
    })

    if (result.error) {
      setError(result.error)
      return null
    }

    setLineup(result.lineup)
    setError('')
    setSuccess(successMessage)

    if (keepSelection) {
      setSelectedIds(normalizedIds)
    } else {
      setSelectedIds([])
    }

    return result.lineup
  }

  const findMemberLocation = useCallback(
    (wrestlerId, sourceLineup = lineup) => {
      const ringIndex = sourceLineup.inRing.findIndex(id => id === wrestlerId)
      if (ringIndex !== -1) {
        return { zone: 'ring', index: ringIndex }
      }

      const backstageIndex = sourceLineup.backstage.findIndex(
        id => id === wrestlerId
      )
      if (backstageIndex !== -1) {
        return { zone: 'backstage', index: backstageIndex }
      }

      return { zone: 'pool', index: null }
    },
    [lineup]
  )

  const getLineupStatusLabel = useCallback(
    (wrestlerId, sourceLineup = lineup) => {
      if (!wrestlerId) return 'Fuori line-up'
      if (sourceLineup.champion === wrestlerId) return 'Main Eventer'

      const location = findMemberLocation(wrestlerId, sourceLineup)
      if (location.zone === 'ring') return `In-Ring ${location.index + 1}`
      if (location.zone === 'backstage') {
        return `Backstage ${location.index + 1}`
      }

      return 'Fuori line-up'
    },
    [findMemberLocation, lineup]
  )

  const normalizeDraftChampion = useCallback(draftLineup => {
    const nextLineup = {
      ...draftLineup,
      inRing: [...draftLineup.inRing],
      backstage: [...draftLineup.backstage]
    }

    if (
      !nextLineup.champion ||
      nextLineup.inRing.includes(nextLineup.champion)
    ) {
      return nextLineup
    }

    return {
      ...nextLineup,
      champion: null,
      mainEventerAssignedAt: null,
      mainEventerExpiresAt: null
    }
  }, [])

  const handleMemberDragStart = (event, wrestlerId) => {
    const ids =
      bulkModeEnabled && selectedIds.includes(wrestlerId)
        ? [...new Set(selectedIds.filter(Boolean))]
        : [wrestlerId]

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        ids,
        source: findMemberLocation(wrestlerId)
      })
    )
    setDragOverTarget('')
  }

  const buildDraggedIds = useCallback(
    wrestlerId =>
      bulkModeEnabled && selectedIds.includes(wrestlerId)
        ? [...new Set(selectedIds.filter(Boolean))]
        : [wrestlerId],
    [bulkModeEnabled, selectedIds]
  )

  const handleDragEnd = () => {
    setDragOverTarget('')
  }

  const legacyHandleDropOnTarget = (event, targetKey) => {
    event.preventDefault()
    event.stopPropagation()

    let ids = []
    try {
      const raw = event.dataTransfer.getData('text/plain')
      if (raw) {
        const parsed = JSON.parse(raw)
        ids = Array.isArray(parsed?.ids) ? parsed.ids.filter(Boolean) : []
      }
    } catch (e) {
      // ignore
    }

    setDragOverTarget('')

    if (ids.length === 0) return

    // Champion drop: gestione speciale con cooldown e sostituzione
    if (targetKey === 'champion') {
      if (ids.length !== 1) {
        setError(
          'Per il Main Eventer puoi spostare un solo wrestler alla volta.'
        )
        return
      }
      const [wrestlerId] = ids
      const cooldownUntil = resolveMainEventerCooldownUntil(wrestlerId)
      if (cooldownUntil) {
        const wrestlerName = getWrestler(wrestlerId)?.name || 'Questo wrestler'
        setError(
          `${wrestlerName} è in pausa da Main Eventer fino al ${cooldownUntil.toLocaleDateString()}.`
        )
        return
      }
      // Se il wrestler è già in un altro slot, lo spostiamo in ring (se non lo è già) oppure lo teniamo
      const currentStatus = getCurrentStatus(wrestlerId)
      let nextLineup = lineup
      if (currentStatus !== 'In_Ring' && currentStatus !== 'Main_Eventer') {
        // Se non è ancora schierato, lo aggiungiamo al primo slot ring libero (o sovrascriviamo l'ultimo)
        const firstEmptyRingIndex = nextLineup.inRing.findIndex(
          id => id === null
        )
        if (firstEmptyRingIndex !== -1) {
          const newInRing = [...nextLineup.inRing]
          newInRing[firstEmptyRingIndex] = wrestlerId
          nextLineup = { ...nextLineup, inRing: newInRing }
        } else {
          // Se tutti i ring sono pieni, sostituiamo l'ultimo (più debole)
          const lastRingIndex = nextLineup.inRing.length - 1
          const bumpedId = nextLineup.inRing[lastRingIndex]
          const newInRing = [...nextLineup.inRing]
          newInRing[lastRingIndex] = wrestlerId
          nextLineup = { ...nextLineup, inRing: newInRing }
          // Il wrestler rimosso torna tra i non assegnati
          if (bumpedId && bumpedId !== wrestlerId) {
            // Non dobbiamo fare nulla: non sarà più in assignedIds
          }
        }
      }
      if (trySetChampion(wrestlerId, nextLineup, true)) {
        setSuccess(
          `${
            getWrestler(wrestlerId)?.name || 'Wrestler'
          } è ora il Main Eventer.`
        )
      }
      return
    }

    // Drop su ring o backstage: usa la logica esistente (riempie slot vuoti)
    const targetStatus =
      targetKey === 'ring'
        ? 'In_Ring'
        : targetKey === 'backstage'
        ? 'Backstage'
        : null
    if (!targetStatus) return

    applyLineupActionForIds(ids, targetStatus, {
      keepSelection: false,
      successMessage: buildActionSuccessMessage(
        ids,
        targetKey === 'ring' ? 'In-Ring' : 'Backstage'
      )
    })
  }
  void legacyHandleDropOnTarget

  const moveSingleWrestlerToSlot = ({
    wrestlerId,
    targetZone,
    targetIndex,
    allowReplaceFromPool = false
  }) => {
    const source = findMemberLocation(wrestlerId)

    if (source.zone === targetZone && source.index === targetIndex) {
      return lineup
    }

    const nextLineup = {
      ...lineup,
      inRing: [...lineup.inRing],
      backstage: [...lineup.backstage]
    }
    const targetList =
      targetZone === 'ring' ? nextLineup.inRing : nextLineup.backstage
    const targetId = targetList[targetIndex] || null

    if (source.zone === 'pool' && targetId && !allowReplaceFromPool) {
      setError(
        'Lo slot e occupato. Libera prima lo slot oppure trascina sulla sezione per usare solo gli spazi liberi.'
      )
      return null
    }

    if (source.zone === 'ring') {
      nextLineup.inRing[source.index] = null
    }
    if (source.zone === 'backstage') {
      nextLineup.backstage[source.index] = null
    }

    targetList[targetIndex] = wrestlerId

    if (targetId && targetId !== wrestlerId) {
      if (source.zone === 'ring') {
        nextLineup.inRing[source.index] = targetId
      } else if (source.zone === 'backstage') {
        nextLineup.backstage[source.index] = targetId
      }
    }

    const normalizedLineup = normalizeDraftChampion(nextLineup)

    if (lineup.champion === wrestlerId && targetZone === 'ring') {
      normalizedLineup.champion = wrestlerId
      normalizedLineup.mainEventerAssignedAt =
        lineup.mainEventerAssignedAt || null
      normalizedLineup.mainEventerExpiresAt =
        lineup.mainEventerExpiresAt || null
    } else if (lineup.champion === targetId) {
      const championLocation = findMemberLocation(targetId, normalizedLineup)

      if (championLocation.zone === 'ring') {
        normalizedLineup.champion = targetId
        normalizedLineup.mainEventerAssignedAt =
          lineup.mainEventerAssignedAt || null
        normalizedLineup.mainEventerExpiresAt =
          lineup.mainEventerExpiresAt || null
      }
    }

    return normalizedLineup
  }

  const readDragPayload = event => {
    try {
      const raw = event.dataTransfer.getData('text/plain')
      if (!raw) return { ids: [], source: null }

      const parsed = JSON.parse(raw)
      return {
        ids: Array.isArray(parsed?.ids)
          ? [...new Set(parsed.ids.filter(Boolean))]
          : [],
        source: parsed?.source || null
      }
    } catch (dragError) {
      return { ids: [], source: null }
    }
  }

  const getDropTargetFromElement = element => {
    const targetElement = element?.closest?.('[data-lineup-drop-key]')
    if (!targetElement) return null

    const rawIndex = targetElement.dataset.lineupDropIndex
    const parsedIndex =
      rawIndex === undefined || rawIndex === ''
        ? null
        : Number.parseInt(rawIndex, 10)

    return {
      key: targetElement.dataset.lineupDropKey || '',
      zone: targetElement.dataset.lineupDropZone || '',
      mode: targetElement.dataset.lineupDropMode || 'panel',
      index: Number.isNaN(parsedIndex) ? null : parsedIndex
    }
  }

  const getDropTargetFromPoint = (clientX, clientY) => {
    if (typeof document === 'undefined') return null
    return getDropTargetFromElement(document.elementFromPoint(clientX, clientY))
  }

  const updateTouchHoverTarget = (clientX, clientY) => {
    const target = getDropTargetFromPoint(clientX, clientY)
    setDragOverTarget(target?.key || '')
    return target
  }

  const handleTouchDragClickCapture = event => {
    if (!touchDragRef.current.suppressClick) return

    event.preventDefault()
    event.stopPropagation()
    touchDragRef.current.suppressClick = false
  }

  const resetTouchDrag = () => {
    touchDragRef.current = {
      wrestlerId: null,
      ids: [],
      dragging: false,
      suppressClick: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0
    }
    setTouchDragPreview(null)
    setDragOverTarget('')
  }

  const dropIdsOnTarget = (ids, target) => {
    if (ids.length === 0) return

    if (target.zone === 'champion') {
      if (ids.length !== 1) {
        setError(
          'Per il Main Eventer puoi spostare un solo wrestler alla volta.'
        )
        return
      }

      const [wrestlerId] = ids
      const currentStatus = getCurrentStatus(wrestlerId)
      let nextChampionLineup = lineup

      if (currentStatus !== 'In_Ring' && currentStatus !== 'Main_Eventer') {
        const ringPlacement = applyBulkLineupAction({
          lineup,
          selectedIds: [wrestlerId],
          targetStatus: 'In_Ring',
          maxRing,
          maxBackstage
        })

        if (ringPlacement.error) {
          setError(
            'Per promuovere un wrestler a Main Eventer serve prima uno spazio libero in In-Ring.'
          )
          return
        }

        nextChampionLineup = ringPlacement.lineup
      }

      if (trySetChampion(wrestlerId, nextChampionLineup, true)) {
        setSuccess(
          currentStatus === 'In_Ring' || currentStatus === 'Main_Eventer'
            ? `${
                getWrestler(wrestlerId)?.name || 'Wrestler'
              } e ora il Main Eventer.`
            : `${
                getWrestler(wrestlerId)?.name || 'Wrestler'
              } e stato aggiunto in In-Ring e promosso a Main Eventer.`
        )
      }
      return
    }

    if (target.mode === 'slot') {
      if (ids.length !== 1) {
        setError(
          'Per scambiare uno slot specifico trascina un solo wrestler alla volta.'
        )
        return
      }

      const nextLineup = moveSingleWrestlerToSlot({
        wrestlerId: ids[0],
        targetZone: target.zone,
        targetIndex: target.index
      })

      if (!nextLineup) {
        return
      }

      setLineup(nextLineup)
      setError('')
      setSuccess(
        buildActionSuccessMessage(
          ids,
          target.zone === 'ring' ? 'In-Ring' : 'Backstage'
        )
      )
      setSelectedIds(bulkModeEnabled ? ids : [])
      return
    }

    if (target.zone === 'bench') {
      applyLineupActionForIds(ids, 'UNASSIGNED', {
        keepSelection: false,
        successMessage: buildActionSuccessMessage(ids, 'fuori dalla line-up')
      })
      return
    }

    const targetStatus =
      target.zone === 'ring'
        ? 'In_Ring'
        : target.zone === 'backstage'
        ? 'Backstage'
        : null
    if (!targetStatus) return

    const result = applyLineupActionForIds(ids, targetStatus, {
      keepSelection: bulkModeEnabled,
      successMessage: buildActionSuccessMessage(
        ids,
        target.zone === 'ring' ? 'In-Ring' : 'Backstage'
      )
    })

    if (
      !result &&
      ids.length === 1 &&
      findMemberLocation(ids[0]).zone !== 'pool'
    ) {
      setError(
        'Se la sezione e piena, trascina il wrestler su uno slot specifico per scambiarlo.'
      )
    }
  }

  const handleDropOnTarget = (event, target) => {
    event.preventDefault()
    event.stopPropagation()

    const { ids } = readDragPayload(event)
    setDragOverTarget('')

    dropIdsOnTarget(ids, target)
  }

  const handleDragOverTarget = (event, targetKey) => {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'move'
    if (dragOverTarget !== targetKey) {
      setDragOverTarget(targetKey)
    }
  }

  const handleTouchDragStart = (event, wrestlerId) => {
    if (!wrestlerId || event.target.closest('button')) return

    const touch = event.touches?.[0]
    if (!touch) return

    const ids = buildDraggedIds(wrestlerId)
    const wrestlerName = getWrestler(wrestlerId)?.name || 'Wrestler'

    touchDragRef.current = {
      wrestlerId,
      ids,
      dragging: false,
      suppressClick: false,
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY
    }

    setTouchDragPreview({
      label:
        ids.length > 1 ? `${wrestlerName} +${ids.length - 1}` : wrestlerName,
      count: ids.length,
      x: touch.clientX,
      y: touch.clientY,
      active: false
    })
  }

  const handleTouchDragMove = event => {
    const touch = event.touches?.[0]
    const session = touchDragRef.current

    if (!touch || !session.wrestlerId) return

    session.lastX = touch.clientX
    session.lastY = touch.clientY

    const distance = Math.hypot(
      touch.clientX - session.startX,
      touch.clientY - session.startY
    )

    if (!session.dragging && distance < 12) {
      return
    }

    event.preventDefault()

    if (!session.dragging) {
      session.dragging = true
      session.suppressClick = true
    }

    updateTouchHoverTarget(touch.clientX, touch.clientY)
    setTouchDragPreview(prev =>
      prev
        ? {
            ...prev,
            x: touch.clientX,
            y: touch.clientY,
            active: true
          }
        : prev
    )
  }

  const handleTouchDragEnd = event => {
    const session = touchDragRef.current
    if (!session.wrestlerId) {
      resetTouchDrag()
      return
    }

    const changedTouch = event.changedTouches?.[0]
    const clientX = changedTouch?.clientX ?? session.lastX
    const clientY = changedTouch?.clientY ?? session.lastY

    if (session.dragging) {
      event.preventDefault()
      const target = updateTouchHoverTarget(clientX, clientY)

      if (target) {
        dropIdsOnTarget(session.ids, target)
      }
    }

    const shouldSuppressClick = session.suppressClick
    resetTouchDrag()

    if (shouldSuppressClick) {
      touchDragRef.current.suppressClick = true
      window.setTimeout(() => {
        touchDragRef.current.suppressClick = false
      }, 0)
    }
  }

  const handleControlledTouchContextMenu = event => {
    event.preventDefault()
    event.stopPropagation()
  }
  // ================================================================

  const applyBulkAction = targetStatus => {
    applyLineupActionForIds(selectedIds, targetStatus, {
      keepSelection: true,
      successMessage: buildActionSuccessMessage(
        selectedIds,
        targetStatus === 'In_Ring'
          ? 'In-Ring'
          : targetStatus === 'Backstage'
          ? 'Backstage'
          : 'fuori dalla line-up'
      )
    })
  }

  const handleQuickMainEventer = () => {
    if (selectedIds.length !== 1) {
      setError('Seleziona un solo wrestler per impostarlo come Main Eventer.')
      return
    }

    const [selectedId] = selectedIds
    if (
      getCurrentStatus(selectedId) !== 'In_Ring' &&
      getCurrentStatus(selectedId) !== 'Main_Eventer'
    ) {
      setError('Il Main Eventer deve essere già schierato In-Ring.')
      return
    }

    trySetChampion(selectedId, lineup, true)
  }

  const handleSelectWrestler = wrestler => {
    if (modalType === 'champion') {
      if (!trySetChampion(wrestler.id)) {
        return
      }
    } else if (modalType === 'ring' && modalSlotIndex !== null) {
      const nextLineup = moveSingleWrestlerToSlot({
        wrestlerId: wrestler.id,
        targetZone: 'ring',
        targetIndex: modalSlotIndex,
        allowReplaceFromPool: true
      })

      if (!nextLineup) return
      setLineup(nextLineup)
    } else if (modalType === 'backstage' && modalSlotIndex !== null) {
      const nextLineup = moveSingleWrestlerToSlot({
        wrestlerId: wrestler.id,
        targetZone: 'backstage',
        targetIndex: modalSlotIndex,
        allowReplaceFromPool: true
      })

      if (!nextLineup) return
      setLineup(nextLineup)
    }

    setError('')
    closeModal()
  }

  const handleRemoveFromSlot = () => {
    if (modalType === 'champion') {
      setLineup(prev => ({
        ...prev,
        champion: null,
        mainEventerAssignedAt: null,
        mainEventerExpiresAt: null
      }))
    } else if (modalType === 'ring' && modalSlotIndex !== null) {
      const nextInRing = [...lineup.inRing]
      const removedId = nextInRing[modalSlotIndex]
      nextInRing[modalSlotIndex] = null

      setLineup(prev => ({
        ...prev,
        inRing: nextInRing,
        champion: prev.champion === removedId ? null : prev.champion,
        mainEventerAssignedAt:
          prev.champion === removedId ? null : prev.mainEventerAssignedAt,
        mainEventerExpiresAt:
          prev.champion === removedId ? null : prev.mainEventerExpiresAt
      }))
    } else if (modalType === 'backstage' && modalSlotIndex !== null) {
      const nextBackstage = [...lineup.backstage]
      nextBackstage[modalSlotIndex] = null
      setLineup(prev => ({ ...prev, backstage: nextBackstage }))
    }

    setError('')
    closeModal()
  }

  const handleClearAll = () => {
    setLineup(emptyLineup)
    setSelectedIds([])
    setError('')
    setSuccess('')
  }

  const handleApplyAutoLineup = () => {
    if (!isPremium) {
      setError('Auto line-up e disponibile solo per gli utenti Premium.')
      setSuccess('')
      return
    }

    if (premiumInsightsLoading) {
      setError('Sto caricando i dati Premium per calcolare la line-up.')
      setSuccess('')
      return
    }

    if (!lineupRoleAdvice.hasAny) {
      setError('Non ci sono ancora dati sufficienti per suggerire una line-up.')
      setSuccess('')
      return
    }

    const uniqueIds = []
    const addUniqueId = wrestlerId => {
      if (wrestlerId && !uniqueIds.includes(wrestlerId)) {
        uniqueIds.push(wrestlerId)
      }
    }
    const padSlots = (ids, size) => {
      const nextSlots = ids.slice(0, size)
      while (nextSlots.length < size) nextSlots.push(null)
      return nextSlots
    }

    const championId = hasMainEventer
      ? lineupRoleAdvice.mainEventer?.member?.id || null
      : null
    const backstageIds = lineupRoleAdvice.backstage
      .map(item => item.member.id)
      .filter(wrestlerId => wrestlerId && wrestlerId !== championId)
      .slice(0, maxBackstage)

    backstageIds.forEach(addUniqueId)
    addUniqueId(championId)

    const ringIds = []
    if (championId) {
      ringIds.push(championId)
    }

    lineupRoleAdvice.inRing.forEach(item => {
      const wrestlerId = item.member.id
      if (wrestlerId && !uniqueIds.includes(wrestlerId)) {
        ringIds.push(wrestlerId)
        addUniqueId(wrestlerId)
      }
    })

    members.forEach(member => {
      if (ringIds.length >= maxRing) return
      if (!uniqueIds.includes(member.id)) {
        ringIds.push(member.id)
        addUniqueId(member.id)
      }
    })

    const nextLineup = normalizeDraftChampion({
      ...lineup,
      champion: championId,
      inRing: padSlots(ringIds, maxRing),
      backstage: padSlots(backstageIds, maxBackstage),
      mainEventerAssignedAt:
        championId && championId === persistedLineup?.champion
          ? persistedLineup.mainEventerAssignedAt || null
          : null,
      mainEventerExpiresAt:
        championId && championId === persistedLineup?.champion
          ? persistedLineup.mainEventerExpiresAt || null
          : null
    })

    setLineup(nextLineup)
    setSelectedIds([])
    setError('')
    setSuccess(
      'Auto line-up applicata. Puoi modificarla liberamente prima di salvare.'
    )
  }

  const handleSave = async () => {
    if (!isComplete()) {
      setError('Devi assegnare tutti i wrestler tra In-Ring e Backstage.')
      return
    }

    if (
      hasMainEventer &&
      lineup.champion &&
      !assignedInRingIds.includes(lineup.champion)
    ) {
      setError(
        'Il Main Eventer deve essere uno dei wrestler schierati In-Ring.'
      )
      return
    }

    if (lineup.champion) {
      const cooldownUntil = resolveMainEventerCooldownUntil(lineup.champion)
      if (cooldownUntil) {
        const wrestlerName =
          getWrestler(lineup.champion)?.name || 'Questo wrestler'
        setError(
          `${wrestlerName} è in pausa da Main Eventer fino al ${cooldownUntil.toLocaleDateString()}.`
        )
        return
      }
    }

    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const statusMap = {}
      assignedInRingIds.forEach(wrestlerId => {
        statusMap[wrestlerId] =
          hasMainEventer && wrestlerId === lineup.champion
            ? 'Main_Eventer'
            : 'In_Ring'
      })
      assignedBackstageIds.forEach(wrestlerId => {
        statusMap[wrestlerId] = 'Backstage'
      })

      const effectiveFrom = new Date()
      const wrestlerScoresById = members.reduce((acc, member) => {
        acc[member.id] = member.score ?? member.currentScore ?? 0
        return acc
      }, {})
      const { members: updatedWrestlers, accumulatedPointsDelta } =
        settleFactionMembersForStatusChanges({
          faction,
          roster,
          members: getFactionStoredWrestlers(faction),
          statusByWrestlerId: statusMap,
          wrestlerScoresById,
          referenceDate: effectiveFrom
        })
      const nextAccumulatedPoints =
        (faction.accumulatedPoints || 0) + accumulatedPointsDelta

      const lineupUpdate = buildFactionLineupUpdateAfterSwap({
        faction,
        roster: lineupConfig,
        updatedWrestlers,
        now: effectiveFrom,
        preferPreviousChampion: false
      }) || {
        lineup: {
          champion: hasMainEventer ? lineup.champion : null,
          inRing: assignedInRingIds,
          backstage: assignedBackstageIds
        },
        currentLineup: null,
        lineupHistory: Array.isArray(faction.lineupHistory)
          ? faction.lineupHistory
          : []
      }
      const payload = {
        ...buildFactionMemberFields(updatedWrestlers),
        accumulatedPoints: nextAccumulatedPoints,
        ...lineupUpdate,
        updatedAt: Timestamp.now()
      }

      await updateDoc(doc(db, 'factions', faction.id), payload)
      if (lineupUpdate?.currentLineup) {
        setLineup(
          clearExpiredChampion(normalizeLineup(lineupUpdate.currentLineup))
        )
      }
      onSaved?.({ id: faction.id, ...payload })
      setSuccess('Line-Up salvata con successo!')
      window.setTimeout(() => setSuccess(''), 2000)
    } catch (err) {
      console.error('Errore salvataggio line-up:', err)
      setError('Errore durante il salvataggio della line-up.')
    } finally {
      setSaving(false)
    }
  }

  const Slot = ({ wrestlerId, onClick, dropTarget }) => {
    const wrestler = wrestlerId ? getWrestler(wrestlerId) : null
    const isInjured = Boolean(wrestler?.isInjured)
    const isSelected = Boolean(wrestlerId && selectedIds.includes(wrestlerId))
    const isDropActive = Boolean(
      dropTarget && dragOverTarget === dropTarget.key
    )

    return (
      <div
        className={`slot-card ${isInjured ? 'slot-card--injured' : ''} ${
          isSelected ? 'slot-card--selected' : ''
        } ${isDropActive ? 'slot-card--drop-active' : ''}`}
        onClick={onClick}
        onClickCapture={handleTouchDragClickCapture}
        onContextMenu={handleControlledTouchContextMenu}
        draggable={Boolean(wrestlerId)}
        onDragStart={event =>
          wrestlerId && handleMemberDragStart(event, wrestlerId)
        }
        onDragEnd={handleDragEnd}
        onDragOver={event =>
          dropTarget && handleDragOverTarget(event, dropTarget.key)
        }
        onDragLeave={() => setDragOverTarget('')}
        onDrop={event => dropTarget && handleDropOnTarget(event, dropTarget)}
        onTouchStart={event =>
          wrestlerId && handleTouchDragStart(event, wrestlerId)
        }
        onTouchMove={handleTouchDragMove}
        onTouchEnd={handleTouchDragEnd}
        onTouchCancel={resetTouchDrag}
        data-lineup-drop-key={dropTarget?.key || ''}
        data-lineup-drop-zone={dropTarget?.zone || ''}
        data-lineup-drop-mode={dropTarget?.mode || ''}
        data-lineup-drop-index={dropTarget?.index ?? ''}
        style={{
          cursor: wrestlerId ? 'grab' : 'pointer',
          transition: 'transform 0.2s',
          minWidth: '100px',
          borderRadius: '30%'
        }}
      >
        {wrestler ? (
          <>
            <img
              src={wrestler.imageUrl || vacantImg}
              alt={wrestler.name}
              className='lineup-slot-avatar'
              draggable={false}
              onContextMenu={handleControlledTouchContextMenu}
            />
            {isInjured && (
              <span className='lineup-slot-injury-badge'>
                <i className='bi bi-bandaid-fill'></i>
              </span>
            )}
            {isPremium && (
              <span className='lineup-slot-premium-info'>
                <PremiumWrestlerInsightButton
                  wrestler={wrestler}
                  insight={premiumInsights[wrestler.id]}
                  isLoading={premiumInsightsLoading}
                  lineupAdvice={getLineupAdviceForWrestler(wrestler.id)}
                  buttonClassName='premium-info-button--lineup'
                />
              </span>
            )}
          </>
        ) : (
          <>
            <img
              src={vacantImg}
              alt='Vacant'
              className='lineup-slot-avatar lineup-slot-avatar--vacant'
              draggable={false}
              onContextMenu={handleControlledTouchContextMenu}
            />
          </>
        )}
      </div>
    )
  }

  const championDropTarget = {
    key: 'champion',
    zone: 'champion',
    mode: 'panel'
  }
  const ringPanelDropTarget = {
    key: 'ring-panel',
    zone: 'ring',
    mode: 'panel'
  }
  const backstagePanelDropTarget = {
    key: 'backstage-panel',
    zone: 'backstage',
    mode: 'panel'
  }
  const benchDropTarget = {
    key: 'bench',
    zone: 'bench',
    mode: 'panel'
  }
  const modalSlotMeta = getLineupSlotMeta(modalType, modalSlotIndex)
  const modalSelectableWrestlers = useMemo(
    () =>
      modalType === 'champion' ? championCandidates : members,
    [championCandidates, members, modalType]
  )
  const modalSwapCandidates = useMemo(() => {
    const source = [...modalSelectableWrestlers]
    const currentId = currentSlotWrestler?.id || null
    const statusPriority = {
      Main_Eventer: 0,
      In_Ring: 1,
      Backstage: 2,
      null: 3
    }

    return source
      .filter(member => member?.id && member.id !== currentId)
      .sort((a, b) => {
        const statusA = getCurrentStatus(a.id)
        const statusB = getCurrentStatus(b.id)
        const priorityA = statusPriority[statusA ?? 'null']
        const priorityB = statusPriority[statusB ?? 'null']

        if (priorityA !== priorityB) return priorityA - priorityB
        return (a.name || '').localeCompare(b.name || '', 'it')
      })
  }, [currentSlotWrestler?.id, getCurrentStatus, modalSelectableWrestlers])
  const currentSlotStatusLabel = useMemo(
    () =>
      currentSlotWrestler
        ? getLineupStatusLabel(currentSlotWrestler.id)
        : modalType === 'champion'
        ? 'Nessun Main Eventer'
        : 'Slot libero',
    [currentSlotWrestler, getLineupStatusLabel, modalType]
  )

  return (
    <div className='lineup-container bg-brand-dark text-light py-4'>
      <div className='container'>
        <div className='d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4'>
          <div>
            <h5 className='mb-1 text-center text-lg-start brand-title'>
              Line-Up Wrestlers
            </h5>
            <div className='text-muted-brand small text-center text-lg-start'>
              {isInitialPlacementFlow
                ? 'Se stai creando la line-up per la prima volta, trovi in fondo tutti i wrestler ancora da piazzare.'
                : 'La line-up è gia impostata: puoi trascinare i wrestler tra le sezioni per spostarli rapidamente.'}
            </div>
          </div>
        </div>

        <div className='card brand-card border-0 shadow-sm mb-4 lineup-flow-card small'>
          <div className='card-body d-flex flex-column flex-xl-row justify-content-between gap-3 bg-brand-dark border border-1 rounded-3'>
            <div>
              <h6 className='mb-1 text-brand-primary-ultralight'>
                <i className='bi bi-info-circle-fill me-2'></i>
                {isInitialPlacementFlow
                  ? 'Procedura semplificata di inserimento'
                  : 'Sposta la line-up in pochi passaggi'}
              </h6>
              <div className='text-muted-brand small'>
                {isInitialPlacementFlow
                  ? `Trascina i wrestler dal fondo verso In-Ring${
                      maxBackstage > 0 ? ' e Backstage' : ''
                    } oppure usa i pulsanti rapidi sulle card.`
                  : 'Trascina un wrestler su una sezione per riempire gli spazi liberi, oppure su uno slot specifico per scambiarlo al volo.'}
              </div>
            </div>
          </div>
        </div>

        {showInjuryBanner && (
          <div className='faction-injury-banner mb-4' role='alert'>
            <div className='faction-injury-banner__content'>
              <i className='bi bi-bandaid-fill' aria-hidden='true'></i>
              <div>
                <strong>
                  {hasBackstage && injuredMembersOutsideBackstage.length > 0
                    ? 'Sposta gli infortunati in backstage'
                    : 'Hai wrestler infortunati in fazione'}
                </strong>
                <span>
                  {hasBackstage && injuredMembersOutsideBackstage.length > 0 ? (
                    <>
                      {injuredMembersOutsideBackstage.length === 1
                        ? 'Questo wrestler e ancora schierato'
                        : 'Questi wrestler sono ancora schierati'}
                      {injuredMembersLabel ? `: ${injuredMembersLabel}.` : '.'}{' '}
                      {injuredMembersOutsideBackstage.length === 1
                        ? 'Spostalo in backstage per non lasciarlo schierato.'
                        : 'Spostali in backstage per non lasciarli schierati.'}
                    </>
                  ) : (
                    <>
                      {injuredMembers.length === 1
                        ? 'Wrestler infortunato'
                        : 'Wrestler infortunati'}
                      {injuredMembersLabel ? `: ${injuredMembersLabel}.` : '.'}
                    </>
                  )}
                </span>
              </div>
            </div>
            <div className='faction-injury-banner__actions'>
              <button
                type='button'
                className='btn btn-sm btn-outline-light faction-injury-banner__dismiss'
                onClick={handleHideInjuryBannerPermanently}
              >
                Non mostrare piu
              </button>
              <button
                type='button'
                className='faction-injury-banner__close'
                aria-label='Chiudi'
                onClick={() => setInjuryBannerClosed(true)}
              >
                <i className='bi bi-x-lg' aria-hidden='true'></i>
              </button>
            </div>
          </div>
        )}
        {renderLineupPremiumPreview()}
        {isPremium && (
          <div className='lineup-premium-actions'>
            <button
              type='button'
              className='btn btn-sm btn-outline-brand-secondary lineup-auto-button'
              onClick={handleApplyAutoLineup}
              disabled={
                saving || premiumInsightsLoading || !lineupRoleAdvice.hasAny
              }
            >
              <i className='bi bi-stars me-1' aria-hidden='true'></i>
              Auto line-up
            </button>
          </div>
        )}

        {hasMainEventer && (
          <div className='row mb-2'>
            <div className='col-12'>
              <div className='card brand-card border-0 shadow-sm bg-transparent'>
                <div
                  className={`card-body text-center p-3 position-relative lineup-drop-panel ${
                    dragOverTarget === championDropTarget.key
                      ? 'lineup-drop-panel--active'
                      : ''
                  }`}
                  onDragOver={event =>
                    handleDragOverTarget(event, championDropTarget.key)
                  }
                  onDragLeave={() => setDragOverTarget('')}
                  onDrop={event =>
                    handleDropOnTarget(event, championDropTarget)
                  }
                  data-lineup-drop-key={championDropTarget.key}
                  data-lineup-drop-zone={championDropTarget.zone}
                  data-lineup-drop-mode={championDropTarget.mode}
                >
                  <div
                    className='position-relative d-inline-block'
                    style={{ zIndex: 2 }}
                  >
                    <img
                      src={beltImg}
                      alt='Cintura'
                      className='position-absolute'
                      draggable={false}
                      onContextMenu={handleControlledTouchContextMenu}
                      style={{
                        bottom: '-40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        maxHeight: '70px',
                        objectFit: 'contain',
                        zIndex: 4,
                        opacity: lineup.champion ? 1 : 0.35,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                        pointerEvents: 'none'
                      }}
                    />
                    <Slot
                      wrestlerId={lineup.champion}
                      onClick={() => handleSlotClick('champion')}
                      dropTarget={championDropTarget}
                    />
                  </div>
                  <div
                    className='mt-3 text-muted-brand'
                    style={{ fontSize: '0.8rem' }}
                  >
                    {hasPendingChampionChange && savedChampion
                      ? `Main eventer salvato: ${savedChampion.name}${
                          savedChampionExpiryText
                            ? `, ${savedChampionExpiryText}`
                            : ''
                        }. ${
                          draftChampion
                            ? `Il cambio a ${draftChampion.name} partira al salvataggio.`
                            : 'La rimozione partira al salvataggio.'
                        }`
                      : !lineup.champion &&
                        savedChampion &&
                        savedChampionCooldownUntil
                      ? `${
                          savedChampion.name
                        } è in pausa da Main Eventer fino al ${savedChampionCooldownUntil.toLocaleDateString()}.`
                      : lineup.champion
                      ? lineup.mainEventerExpiresAt
                        ? `Scade il ${lineup.mainEventerExpiresAt.toLocaleDateString()}`
                        : 'La scadenza partira al salvataggio'
                      : isInitialPlacementFlow
                      ? 'Metti un wrestler in In-Ring e poi trascinalo qui per promuoverlo a Main Eventer.'
                      : 'Trascina qui un wrestler In-Ring per promuoverlo a Main Eventer.'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {maxRing > 0 && (
          <div className='row mb-2'>
            <div className='col-12'>
              <div className='card brand-card border-0 shadow-sm bg-transparent'>
                <div
                  className={`card-body text-center p-3 position-relative lineup-ring-panel lineup-drop-panel ${
                    dragOverTarget === ringPanelDropTarget.key
                      ? 'lineup-drop-panel--active'
                      : ''
                  }`}
                  style={{
                    backgroundImage: `url(${ringImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '0 0 0.375rem 0.375rem'
                  }}
                  onDragOver={event =>
                    handleDragOverTarget(event, ringPanelDropTarget.key)
                  }
                  onDragLeave={() => setDragOverTarget('')}
                  onDrop={event =>
                    handleDropOnTarget(event, ringPanelDropTarget)
                  }
                  data-lineup-drop-key={ringPanelDropTarget.key}
                  data-lineup-drop-zone={ringPanelDropTarget.zone}
                  data-lineup-drop-mode={ringPanelDropTarget.mode}
                >
                  <div className='lineup-panel-overlay'></div>
                  <div
                    className='d-flex flex-wrap gap-2 justify-content-center'
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    {Array.from({ length: maxRing }).map((_, idx) => (
                      <Slot
                        key={`ring-${idx}`}
                        wrestlerId={lineup.inRing[idx]}
                        onClick={() => handleSlotClick('ring', idx)}
                        dropTarget={{
                          key: `ring-slot-${idx}`,
                          zone: 'ring',
                          mode: 'slot',
                          index: idx
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {maxBackstage > 0 && (
          <div className='row mb-2'>
            <div className='col-12'>
              <div className='card brand-card border-0 shadow-sm bg-transparent'>
                <div
                  className={`card-body text-center p-3 position-relative lineup-backstage-panel lineup-drop-panel ${
                    dragOverTarget === backstagePanelDropTarget.key
                      ? 'lineup-drop-panel--active'
                      : ''
                  }`}
                  style={{
                    backgroundImage: `url(${backstageImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '0 0 0.375rem 0.375rem'
                  }}
                  onDragOver={event =>
                    handleDragOverTarget(event, backstagePanelDropTarget.key)
                  }
                  onDragLeave={() => setDragOverTarget('')}
                  onDrop={event =>
                    handleDropOnTarget(event, backstagePanelDropTarget)
                  }
                  data-lineup-drop-key={backstagePanelDropTarget.key}
                  data-lineup-drop-zone={backstagePanelDropTarget.zone}
                  data-lineup-drop-mode={backstagePanelDropTarget.mode}
                >
                  <div className='lineup-panel-overlay'></div>
                  <div
                    className='d-flex flex-wrap gap-2 justify-content-center'
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    {Array.from({ length: maxBackstage }).map((_, idx) => (
                      <Slot
                        key={`backstage-${idx}`}
                        wrestlerId={lineup.backstage[idx]}
                        onClick={() => handleSlotClick('backstage', idx)}
                        dropTarget={{
                          key: `backstage-slot-${idx}`,
                          zone: 'backstage',
                          mode: 'slot',
                          index: idx
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {unassignedWrestlers.length > 0 && (
          <div className='row mt-4'>
            <div className='col-12'>
              <div className='card brand-card border-0 shadow-sm'>
                <div
                  className={`card-body bg-brand-dark ${
                    dragOverTarget === benchDropTarget.key
                      ? 'lineup-dropzone lineup-dropzone--active'
                      : ''
                  }`}
                  onDragOver={event =>
                    handleDragOverTarget(event, benchDropTarget.key)
                  }
                  onDragLeave={() => setDragOverTarget('')}
                  onDrop={event => handleDropOnTarget(event, benchDropTarget)}
                  data-lineup-drop-key={benchDropTarget.key}
                  data-lineup-drop-zone={benchDropTarget.zone}
                  data-lineup-drop-mode={benchDropTarget.mode}
                >
                  <div className='d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3'>
                    <div>
                      <h6 className='mb-1 brand-title'>
                        <i className='bi bi-people me-2'></i>
                        {isInitialPlacementFlow
                          ? `Wrestler da piazzare (${unassignedWrestlers.length})`
                          : `Fuori line-up (${unassignedWrestlers.length})`}
                      </h6>
                      <div className='text-muted-brand small'>
                        {isInitialPlacementFlow
                          ? 'Trascina questi wrestler nelle sezioni sopra oppure usa i pulsanti rapidi per completarli piu velocemente.'
                          : 'Questi wrestler non sono schierati. Puoi reinserirli sopra o trascinare qui un wrestler dalla line-up per rimuoverlo.'}
                      </div>
                    </div>
                  </div>
                  <div className='row g-2 mt-3'>
                    {unassignedWrestlers.map(member => {
                      const cooldownUntil = resolveMainEventerCooldownUntil(
                        member.id
                      )
                      const ringSlotsLeft = maxRing - assignedInRingIds.length
                      const backstageSlotsLeft =
                        maxBackstage - assignedBackstageIds.length

                      return (
                        <div
                          key={member.id}
                          className='col-6 col-sm-4 col-md-3 col-lg-2'
                        >
                          <div
                            className='lineup-pool-card'
                            style={{
                              backgroundImage: `url(${
                                member.imageUrl || vacantImg
                              })`
                            }}
                            draggable
                            onClickCapture={handleTouchDragClickCapture}
                            onContextMenu={handleControlledTouchContextMenu}
                            onDragStart={event =>
                              handleMemberDragStart(event, member.id)
                            }
                            onDragEnd={handleDragEnd}
                            onTouchStart={event =>
                              handleTouchDragStart(event, member.id)
                            }
                            onTouchMove={handleTouchDragMove}
                            onTouchEnd={handleTouchDragEnd}
                            onTouchCancel={resetTouchDrag}
                          >
                            {/* Overlay scuro per leggibilità */}
                            <div className='lineup-pool-overlay'></div>

                            {/* Contenuto sopra lo sfondo */}
                            <div className='lineup-pool-content'>
                              <div className='lineup-pool-badges'>
                                {member.isInjured && (
                                  <span
                                    className='badge injured'
                                    title='Infortunato'
                                  >
                                    <i className='bi bi-bandaid-fill'></i>
                                  </span>
                                )}
                                {cooldownUntil && (
                                  <span
                                    className='badge cooldown'
                                    title={`Pausa ME fino al ${cooldownUntil.toLocaleDateString()}`}
                                  >
                                    <i className='bi bi-hourglass-split'></i>
                                  </span>
                                )}
                                {isPremium && (
                                  <PremiumWrestlerInsightButton
                                    wrestler={member}
                                    insight={premiumInsights[member.id]}
                                    isLoading={premiumInsightsLoading}
                                    lineupAdvice={getLineupAdviceForWrestler(
                                      member.id
                                    )}
                                    buttonClassName='premium-info-button--lineup'
                                  />
                                )}
                              </div>
                              <div className='lineup-pool-name'>
                                {member.name}
                              </div>
                              <div className='lineup-pool-actions'>
                                {maxBackstage > 0 && (
                                  <button
                                    type='button'
                                    className='action-btn text-brand-primary-ultralight'
                                    onClick={() =>
                                      handleQuickAssign(member.id, 'Backstage')
                                    }
                                    disabled={backstageSlotsLeft === 0}
                                    title={
                                      backstageSlotsLeft === 0
                                        ? 'Nessuno slot Backstage disponibile'
                                        : 'Assegna a Backstage'
                                    }
                                  >
                                    Backstage
                                  </button>
                                )}
                                <button
                                  type='button'
                                  className='action-btn text-brand-secondary'
                                  onClick={() =>
                                    handleQuickAssign(member.id, 'In_Ring')
                                  }
                                  disabled={ringSlotsLeft === 0}
                                  title={
                                    ringSlotsLeft === 0
                                      ? 'Nessuno slot Ring disponibile'
                                      : 'Assegna a In-Ring'
                                  }
                                >
                                  In-Ring
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='mt-4 roster-form-actions'>
          <div>
            {error && (
              <div className='alert alert-danger mb-0 bg-danger bg-opacity-20 text-light border-0'>
                {error}
              </div>
            )}
            {success && (
              <div className='alert alert-success mb-0 bg-success bg-opacity-20 text-light border-0'>
                {success}
              </div>
            )}
          </div>
          <div className='roster-form-actions__group'>
            <button
              type='button'
              className='btn roster-form-btn roster-form-btn--ghost'
              onClick={handleClearAll}
              disabled={saving}
            >
              <i className='bi bi-trash me-1'></i>
              Rimuovi tutti
            </button>
            <button
              type='button'
              className='btn btn-brand-secondary roster-form-btn roster-form-btn--primary'
              onClick={handleSave}
              disabled={saving || !isComplete()}
            >
              {saving ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2'></span>
                  Salvataggio...
                </>
              ) : (
                <>
                  <i className='bi bi-save me-2'></i>
                  Salva Line-Up
                </>
              )}
            </button>
          </div>
        </div>

        {renderLineupAdviceCard('lineup-advice-card--bottom mt-4')}

        {touchDragPreview?.active && (
          <div
            className='lineup-touch-preview'
            style={{
              left: touchDragPreview.x,
              top: touchDragPreview.y
            }}
          >
            <span className='lineup-touch-preview__label'>
              {touchDragPreview.label}
            </span>
            {touchDragPreview.count > 1 && (
              <span className='lineup-touch-preview__count'>
                {touchDragPreview.count}
              </span>
            )}
          </div>
        )}

        {/* Modal rimasto identico (non riportato per brevità, ma invariato) */}
        {modalOpen && modalType === 'bulk' && (
          <div
            className='modal fade show d-block'
            style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050 }}
            tabIndex='-1'
          >
            <div className='modal-dialog modal-dialog-centered modal-xl'>
              <div className='modal-content bg-brand-dark text-light border border-brand-secondary'>
                <div className='modal-header border-secondary'>
                  <h5 className='modal-title brand-title'>
                    Modifica rapida line-up
                  </h5>
                  <button
                    type='button'
                    className='btn-close btn-close-white'
                    onClick={closeModal}
                  ></button>
                </div>
                <div className='modal-body'>
                  {/* ...contenuto identico a prima... */}
                  <div className='d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3'>
                    <div className='text-muted-brand small'>
                      Seleziona più wrestler dal modale e applica il ruolo
                      desiderato in un solo passaggio.
                    </div>
                    <div className='d-flex flex-wrap gap-2'>
                      <span className='badge bg-brand-secondary text-brand-primary'>
                        Selezionati: {selectedIds.length}
                      </span>
                      <span className='badge bg-brand-secondary text-brand-primary'>
                        In-Ring: {assignedInRingIds.length}/{maxRing}
                      </span>
                      {maxBackstage > 0 && (
                        <span className='badge bg-secondary text-brand-primary'>
                          Backstage: {assignedBackstageIds.length}/
                          {maxBackstage}
                        </span>
                      )}
                    </div>
                  </div>

                  {renderLineupAdviceCard('lineup-advice-card--modal mb-3')}

                  <div className='row g-3 align-items-end mb-3'>
                    <div className='col-12 col-lg-4'>
                      <label className='form-label small text-uppercase text-muted-brand mb-2'>
                        Cerca wrestler
                      </label>
                      <input
                        type='text'
                        className='form-control bg-dark text-light border-secondary'
                        placeholder='Nome o brand'
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.target.value)}
                      />
                    </div>
                    <div className='col-12 col-lg-8'>
                      <div className='d-flex flex-wrap gap-2'>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-brand-secondary'
                          onClick={selectAllVisible}
                        >
                          Seleziona visibili
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-brand-secondary'
                          onClick={clearSelection}
                          disabled={selectedIds.length === 0}
                        >
                          Deseleziona
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-light'
                          onClick={() => applyBulkAction('In_Ring')}
                          disabled={selectedIds.length === 0}
                        >
                          In-Ring
                        </button>
                        {maxBackstage > 0 && (
                          <button
                            type='button'
                            className='btn btn-sm btn-outline-light'
                            onClick={() => applyBulkAction('Backstage')}
                            disabled={selectedIds.length === 0}
                          >
                            Backstage
                          </button>
                        )}
                        {hasMainEventer && (
                          <button
                            type='button'
                            className='btn btn-sm btn-outline-warning'
                            onClick={handleQuickMainEventer}
                            disabled={selectedIds.length !== 1}
                          >
                            Main Eventer
                          </button>
                        )}
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-danger'
                          onClick={() => applyBulkAction('UNASSIGNED')}
                          disabled={selectedIds.length === 0}
                        >
                          Rimuovi
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className='row g-3'>
                    {filteredMembers.map(member => {
                      const status = getCurrentStatus(member.id)
                      const isSelected = selectedIds.includes(member.id)
                      const isSavedMainEventer =
                        hasPendingChampionChange &&
                        savedChampionId === member.id
                      const isDraftMainEventer =
                        hasPendingChampionChange &&
                        lineup.champion === member.id
                      const cooldownUntil = resolveMainEventerCooldownUntil(
                        member.id
                      )
                      const advice = getLineupAdviceForWrestler(member.id)

                      return (
                        <div
                          key={member.id}
                          className='col-12 col-md-6 col-xl-4'
                        >
                          <button
                            type='button'
                            className={`card w-100 text-start h-100 border-0 shadow-sm ${
                              isSelected ? 'border border-brand-secondary' : ''
                            }`}
                            onClick={() => toggleSelectedId(member.id)}
                            style={{
                              background: isSelected
                                ? 'rgba(0, 127, 177, 0.18)'
                                : 'rgba(255,255,255,0.04)',
                              color: 'inherit'
                            }}
                          >
                            <div className='card-body d-flex gap-3 align-items-center'>
                              <img
                                src={member.imageUrl || vacantImg}
                                alt={member.name}
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: isSelected
                                    ? '2px solid var(--brand-secondary)'
                                    : '2px solid rgba(255,255,255,0.08)'
                                }}
                              />
                              <div className='flex-grow-1'>
                                <div className='fw-semibold d-flex flex-wrap align-items-center gap-2'>
                                  <span>{member.name}</span>
                                  {member.isInjured && (
                                    <span className='badge text-bg-danger'>
                                      Infortunato
                                    </span>
                                  )}
                                </div>
                                <div className='small text-muted-brand mt-1'>
                                  {member.brand || 'Senza brand'}
                                </div>
                                <div className='d-flex flex-wrap gap-2 mt-2'>
                                  <span
                                    className={`badge ${
                                      status === 'Main_Eventer'
                                        ? 'text-bg-warning'
                                        : status === 'In_Ring'
                                        ? 'text-bg-primary'
                                        : status === 'Backstage'
                                        ? 'text-bg-secondary'
                                        : 'text-bg-dark'
                                    }`}
                                  >
                                    {status === 'Main_Eventer'
                                      ? 'Main Eventer'
                                      : status === 'In_Ring'
                                      ? 'In-Ring'
                                      : status === 'Backstage'
                                      ? 'Backstage'
                                      : 'Non assegnato'}
                                  </span>
                                  {isSavedMainEventer && (
                                    <span className='badge bg-warning bg-opacity-25 text-warning border border-warning border-opacity-25'>
                                      Main Eventer salvato
                                    </span>
                                  )}
                                  {isDraftMainEventer && (
                                    <span className='badge bg-info bg-opacity-25 text-info border border-info border-opacity-25'>
                                      Nuovo main eventer
                                    </span>
                                  )}
                                  {cooldownUntil && (
                                    <span className='badge bg-danger bg-opacity-25 text-danger border border-danger border-opacity-25'>
                                      In pausa fino al{' '}
                                      {cooldownUntil.toLocaleDateString()}
                                    </span>
                                  )}
                                  {advice && (
                                    <span
                                      className={`badge lineup-advice-badge lineup-advice-badge--${advice.primary}`}
                                      title={getLineupAdviceReason(advice)}
                                    >
                                      <i
                                        className='bi bi-stars'
                                        aria-hidden='true'
                                      ></i>
                                      {getLineupAdviceLabel(advice, true)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>
                      )
                    })}

                    {filteredMembers.length === 0 && (
                      <div className='col-12'>
                        <div className='text-center py-4 text-muted-brand'>
                          Nessun wrestler trovato con questo filtro.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='modal-footer border-secondary roster-form-actions'>
                  <button
                    type='button'
                    className='btn roster-form-btn roster-form-btn--secondary'
                    onClick={closeModal}
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {modalOpen && modalType !== 'bulk' && (
          <div
            className='modal fade show d-block lineup-picker-modal-backdrop'
            style={{ backgroundColor: 'rgba(0,0,0,0.76)', zIndex: 1050 }}
            tabIndex='-1'
          >
            <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl lineup-picker-dialog'>
              <div className='modal-content bg-brand-dark text-light border border-brand-secondary lineup-picker-modal'>
                <div className='modal-header border-secondary lineup-picker-modal__header'>
                  <div>
                    <span className='lineup-picker-modal__eyebrow'>
                      Gestione line-up
                    </span>
                    <h5 className='modal-title brand-title mb-1'>
                      {modalType === 'champion'
                        ? 'Scegli il Main Eventer'
                        : modalType === 'ring'
                        ? 'Assegna uno slot In-Ring'
                        : 'Assegna uno slot Backstage'}
                    </h5>
                    <p className='lineup-picker-modal__subtitle mb-0'>
                      {modalSlotMeta.description}
                    </p>
                  </div>
                  <button
                    type='button'
                    className='btn-close btn-close-white'
                    onClick={closeModal}
                  ></button>
                </div>
                <div className='modal-body lineup-picker-modal__body'>
                  {modalType === 'champion' &&
                  championCandidates.length === 0 ? (
                    <div className='alert alert-warning mb-0'>
                      Prima assegna almeno un wrestler agli slot In-Ring, poi
                      potrai scegliere il Main Eventer.
                    </div>
                  ) : (
                    <div className='lineup-swap-modal'>
                      <div className='lineup-swap-modal__selected'>
                        <span className='lineup-picker-status-card__eyebrow'>
                          Wrestler selezionato
                        </span>
                        {currentSlotWrestler ? (
                          <div className='lineup-swap-current-card'>
                            <div className='lineup-swap-current-card__main'>
                              <img
                                src={currentSlotWrestler.imageUrl || vacantImg}
                                alt={currentSlotWrestler.name}
                                className='lineup-swap-current-card__avatar'
                                onError={event => {
                                  event.currentTarget.src = vacantImg
                                }}
                              />
                              <div className='lineup-swap-current-card__copy'>
                                <strong>{currentSlotWrestler.name}</strong>
                                <span>{currentSlotStatusLabel}</span>
                              </div>
                            </div>
                            <div className='lineup-swap-current-card__actions'>
                              {isPremium && (
                                <PremiumWrestlerInsightButton
                                  wrestler={currentSlotWrestler}
                                  insight={
                                    premiumInsights[currentSlotWrestler.id]
                                  }
                                  isLoading={premiumInsightsLoading}
                                  lineupAdvice={getLineupAdviceForWrestler(
                                    currentSlotWrestler.id
                                  )}
                                  buttonClassName='premium-info-button--lineup'
                                />
                              )}
                              <button
                                className='btn btn-sm btn-outline-danger'
                                onClick={handleRemoveFromSlot}
                              >
                                <i className='bi bi-trash me-1'></i>
                                Rimuovi
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className='lineup-swap-empty'>
                            {modalType === 'champion'
                              ? 'Nessun Main Eventer impostato. Scegli chi deve occupare il ruolo.'
                              : 'Slot libero. Scegli un wrestler dalla lista qui sotto.'}
                          </div>
                        )}
                      </div>

                      <div className='lineup-swap-modal__list'>
                        <div className='lineup-swap-modal__list-head'>
                          <div>
                            <span className='lineup-picker-results__eyebrow'>
                              Scambio posizione
                            </span>
                            <h6 className='mb-0'>
                              {currentSlotWrestler
                                ? 'Con quale wrestler vuoi scambiare posizione?'
                                : modalType === 'champion'
                                ? 'Chi vuoi rendere Main Eventer?'
                                : 'Chi vuoi inserire in questo slot?'}
                            </h6>
                            <p className='lineup-swap-modal__helper mb-0'>
                              Ogni wrestler mostra il suo stato attuale nella
                              line-up.
                            </p>
                          </div>
                          <span className='lineup-picker-results__count'>
                            {modalSwapCandidates.length}
                          </span>
                        </div>

                        <div className='lineup-swap-list'>
                          {modalSwapCandidates.map(wrestler => {
                            const cooldownUntil =
                              modalType === 'champion'
                                ? resolveMainEventerCooldownUntil(wrestler.id)
                                : null
                            const isDisabled = Boolean(cooldownUntil)
                            const statusLabel = getLineupStatusLabel(
                              wrestler.id
                            )
                            const advice = getLineupAdviceForWrestler(
                              wrestler.id
                            )

                            return (
                              <div
                                key={wrestler.id}
                                className={`lineup-swap-row ${
                                  isDisabled ? 'is-disabled' : ''
                                }`}
                                role='button'
                                tabIndex={isDisabled ? -1 : 0}
                                aria-disabled={isDisabled}
                                onClick={() => {
                                  if (!isDisabled) {
                                    handleSelectWrestler(wrestler)
                                  }
                                }}
                                onKeyDown={event => {
                                  if (
                                    !isDisabled &&
                                    (event.key === 'Enter' || event.key === ' ')
                                  ) {
                                    event.preventDefault()
                                    handleSelectWrestler(wrestler)
                                  }
                                }}
                              >
                                <span className='lineup-swap-row__main'>
                                  <img
                                    src={wrestler.imageUrl || vacantImg}
                                    alt={wrestler.name}
                                    className='lineup-swap-row__avatar'
                                    onError={event => {
                                      event.currentTarget.src = vacantImg
                                    }}
                                  />
                                  <span className='lineup-swap-row__copy'>
                                    <strong>{wrestler.name}</strong>
                                    <span className='lineup-swap-row__status'>
                                      {statusLabel}
                                    </span>
                                  </span>
                                </span>

                                <span className='lineup-swap-row__aside'>
                                  <span className='lineup-swap-row__badges'>
                                    {wrestler.isInjured && (
                                      <span className='lineup-swap-row__badge lineup-swap-row__badge--danger'>
                                        <i className='bi bi-bandaid-fill'></i>
                                        Infortunato
                                      </span>
                                    )}
                                    {wrestler.active === false && (
                                      <span className='lineup-swap-row__badge lineup-swap-row__badge--muted'>
                                        <i className='bi bi-pause-circle-fill'></i>
                                        Non attivo
                                      </span>
                                    )}
                                    {cooldownUntil && (
                                      <span className='lineup-swap-row__badge lineup-swap-row__badge--warning'>
                                        Fino al{' '}
                                        {cooldownUntil.toLocaleDateString()}
                                      </span>
                                    )}
                                  </span>
                                  <span
                                    className='lineup-swap-row__premium'
                                    onClick={event => event.stopPropagation()}
                                  >
                                    {isPremium && (
                                      <PremiumWrestlerInsightButton
                                        wrestler={wrestler}
                                        insight={premiumInsights[wrestler.id]}
                                        isLoading={premiumInsightsLoading}
                                        lineupAdvice={advice}
                                        buttonClassName='premium-info-button--lineup'
                                      />
                                    )}
                                  </span>
                                  <span className='lineup-swap-row__action'>
                                    <i className='bi bi-arrow-left-right'></i>
                                  </span>
                                </span>
                              </div>
                            )
                          })}

                          {modalSwapCandidates.length === 0 && (
                            <div className='lineup-swap-empty'>
                              {modalType === 'champion'
                                ? 'Non ci sono altri wrestler In-Ring da usare come Main Eventer.'
                                : 'Non ci sono altri wrestler da scambiare con questo slot.'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className='modal-footer border-secondary roster-form-actions'>
                  <button
                    type='button'
                    className='btn roster-form-btn roster-form-btn--secondary'
                    onClick={closeModal}
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {false && modalOpen && modalType !== 'bulk' && (
          <div
            className='modal fade show d-block'
            style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050 }}
            tabIndex='-1'
          >
            <div className='modal-dialog modal-dialog-centered modal-lg'>
              <div className='modal-content bg-brand-dark text-light border border-brand-secondary'>
                <div className='modal-header border-secondary'>
                  <h5 className='modal-title brand-title'>
                    {modalType === 'champion'
                      ? 'Scegli il Main Eventer'
                      : modalType === 'ring'
                      ? 'Scegli un wrestler per l’In-Ring'
                      : 'Scegli un wrestler per il Backstage'}
                  </h5>
                  <button
                    type='button'
                    className='btn-close btn-close-white'
                    onClick={closeModal}
                  ></button>
                </div>
                <div className='modal-body'>
                  {currentSlotWrestler && (
                    <div className='alert d-flex justify-content-between align-items-center text-light border-0'>
                      <span>
                        Attuale: <strong>{currentSlotWrestler.name}</strong>
                      </span>
                      <button
                        className='btn btn-sm btn-outline-danger'
                        onClick={handleRemoveFromSlot}
                      >
                        <i className='bi bi-trash me-1'></i>
                        Rimuovi
                      </button>
                    </div>
                  )}

                  {modalType === 'champion' &&
                  championCandidates.length === 0 ? (
                    <div className='alert alert-warning mb-0'>
                      Prima assegna almeno un wrestler agli slot In-Ring, poi
                      potrai scegliere il Main Eventer.
                    </div>
                  ) : (
                    <div className='row g-3'>
                      {(modalType === 'champion'
                        ? championCandidates
                        : availableWrestlers
                      ).map(wrestler => {
                        const cooldownUntil =
                          modalType === 'champion'
                            ? resolveMainEventerCooldownUntil(wrestler.id)
                            : null
                        const isDisabled = Boolean(cooldownUntil)
                        const advice = getLineupAdviceForWrestler(wrestler.id)

                        return (
                          <div
                            key={wrestler.id}
                            className='col-6 col-sm-4 col-md-3'
                          >
                            <div
                              className={`card h-100 shadow-sm border-0 text-center bg-brand-dark text-light position-relative overflow-hidden ${
                                isDisabled ? 'opacity-75' : ''
                              }`}
                              style={{
                                cursor: isDisabled ? 'not-allowed' : 'pointer'
                              }}
                              onClick={() => {
                                if (!isDisabled) {
                                  handleSelectWrestler(wrestler)
                                }
                              }}
                            >
                              <img
                                src={wrestler.imageUrl || vacantImg}
                                alt={wrestler.name}
                                className='card-img-top lineup-modal-avatar'
                                onError={e => {
                                  e.target.src = vacantImg
                                }}
                              />
                              {isPremium && (
                                <span className='lineup-modal-premium-info'>
                                  <PremiumWrestlerInsightButton
                                    wrestler={wrestler}
                                    insight={premiumInsights[wrestler.id]}
                                    isLoading={premiumInsightsLoading}
                                    lineupAdvice={advice}
                                    buttonClassName='premium-info-button--lineup'
                                  />
                                </span>
                              )}
                              <div className='card-body p-2'>
                                <h6 className='card-title mb-0'>
                                  {wrestler.name}
                                </h6>
                                {advice && (
                                  <small
                                    className={`lineup-modal-advice lineup-modal-advice--${advice.primary}`}
                                  >
                                    <i
                                      className='bi bi-stars'
                                      aria-hidden='true'
                                    ></i>
                                    {getLineupAdviceText(advice)}
                                  </small>
                                )}
                                {cooldownUntil && (
                                  <small
                                    className='d-block text-danger mt-2 fw-bold'
                                    style={{ fontSize: '0.7rem' }}
                                  >
                                    In pausa fino al{' '}
                                    {cooldownUntil.toLocaleDateString()}
                                  </small>
                                )}
                                {wrestler.isInjured && (
                                  <small className='d-inline-flex align-items-center gap-1 text-danger mt-2'>
                                    <i className='bi bi-bandaid-fill'></i>
                                    Infortunato
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}

                      {modalType !== 'champion' &&
                        availableWrestlers.length === 0 && (
                          <div className='col-12 text-center py-3'>
                            <p className='text-muted-brand'>
                              Nessun wrestler disponibile
                            </p>
                          </div>
                        )}
                    </div>
                  )}
                  {renderLineupAdviceCard('lineup-advice-card--modal mb-3')}
                </div>
                <div className='modal-footer border-secondary roster-form-actions'>
                  <button
                    type='button'
                    className='btn roster-form-btn roster-form-btn--secondary'
                    onClick={closeModal}
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditFactionFormLineUp
